import { Helmet } from "react-helmet";
import { useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import classNames from "classnames";
import { gql, useMutation } from "@apollo/client";
import { inspect } from "util";

import Layout from "../../Layout";
import styles from "./index.module.scss";
import PageNotFound from "../../PageNotFound";
import FormContent from "../FormContent";
import SaveHeader from "../SaveHeader";
import ApplicantHeader from "../ApplicantHeader";
import FormNavigation from "../../FormNavigation";
import NotificationBanner from "../../NotificationBanner";
import Summary from "../../Form/Sections/Summary/Summary";
import { sections, summary } from "../../Form/Sections/sections";
import { ClientFormDotKeyType } from "../../../types/clientFormDataType";
import { useClientFileContext } from "../../../contexts/ClientFileProvider";
import StatusScreen from "../../Form/Sections/StatusScreen";
import SubmissionSummary from "../../Form/Sections/SubmissionSummary";
import { useStoreContext } from "../../../contexts/StoreProvider";
import { isAdmin } from "../../../utils/user";

const UPDATE_CLIENT_FILE = gql`
  mutation UpdateClientFile($input: updateClientFileInput) {
    updateClientFile(input: $input) {
      clientFile {
        id
        locked_by {
          id
        }
        locked_at
      }
    }
  }
`;

const AdpForm = () => {
  const { clientFile, formMode: mode } = useClientFileContext();
  const { client_form, status } = clientFile;
  const fileDoesNotExist = clientFile?.client_form === null;
  const methods = useForm({
    mode: "onTouched",
    defaultValues: {
      info: {
        "section2a-show": false,
        "section2b-show": false,
        "section2c-show": false,
        "section2d-show": false,
        "section1-touched": false,
        "section2-touched": false,
        "section2a-touched": false,
        "section2b-touched": false,
        "section2c-touched": false,
        "section2d-touched": false,
        "section3-touched": false,
        "section4Alt-touched": false,
        "section4-touched": false,
      },
      ...client_form?.form_data,
    },
  });
  const name = `${clientFile.client.first_name} ${clientFile.client.last_name}`;
  const healthCard = clientFile.client.health_card;
  const salesRepAssigned = clientFile.client.sales_reps.length > 0;
  const initialSection = mode === "initiateSigning" ? "section4" : "section1";

  const check2a = methods.watch("info.section2a-show");
  const check2b = methods.watch("info.section2b-show");
  const check2c = methods.watch("info.section2c-show");
  const check2d = methods.watch("info.section2d-show");

  const initiateSigningMode = (["initiateSigning", "waitingSigning"].includes(mode) || clientFile.status === "PendingSignature");

  const [selectedSection, setSelectedSection] = useState<string>(initialSection);
  const [viewSummary, setViewSummary] = useState<boolean>(false);
  const [showDebugInfo, setShowDebugInfo] = useState<boolean>(false);
  const [initialized, setInitialized] = useState(false);
  const [viewStatus, setViewStatus] = useState<boolean>(false);

  const { state: { user }, } = useStoreContext();

  useEffect(() => {
    setSelectedSection(initialSection);
  }, [initialSection]);

  const enabledSections = {
    ...sections,
    section1: {
      ...sections.section1,
      disabled: false,
    },
    section2: {
      ...sections.section2,
      disabled: false,
    },
    section2a: {
      ...sections.section2a,
      hidden: !check2a,
      disabled: false,
    },
    section2b: {
      ...sections.section2b,
      hidden: !check2b,
      disabled: false,
    },
    section2c: {
      ...sections.section2c,
      hidden: !check2c,
      disabled: false,
    },
    section2d: {
      ...sections.section2d,
      hidden: !check2d,
      disabled: false,
    },
    section3: {
      ...sections.section3,
      disabled: false,
    },
    section4Alt: {
      ...sections.section4Alt,
      disabled: false,
    },
    section4: {
      ...sections.section4,
      disabled: !initiateSigningMode,
    },
  };

  const onSubmit = () => {
    setViewSummary(true);
  };

  const onError = () => {
    setViewSummary(true);
  };

  useEffect(() => {
    // Trigger touched sections on mount, or all fields if not editing
    if (mode === "edit" || mode === "initiateSigning") {
      const triggerSections = [
        "section1",
        "section2",
        "section2a",
        "section2b",
        "section2c",
        "section2d",
        "section3",
        "section4Alt",
        "section4",
      ].filter(
        (section) => client_form?.form_data?.info?.[`${section}-touched`]
      );
      if (triggerSections.length) {
        methods.trigger(triggerSections as ClientFormDotKeyType[]).then(() => {
          setInitialized(true);
        });
      } else {
        setInitialized(true);
      }
    } else {
      methods.trigger().then(() => {
        setInitialized(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [lockFile] = useMutation(UPDATE_CLIENT_FILE);

  useEffect(() => {
    if (
      ["edit", "review"].includes(mode) && ["Progress", "Review", "Action"].includes(status) && user?.role?.name !== "Sales Rep" &&
        (!clientFile.locked_by || (clientFile.locked_by && clientFile.locked_by.id === user?.id))
    ) {
      lockFile({
        variables: {
          input: {
            where: {
              id: clientFile.id,
            },
            data: {
              locked_at: new Date(),
              locked_by: user?.id,
            },
          },
        },
      });
    }
  }, [mode, clientFile, user, lockFile, status]);

  useEffect(() => {
    if (viewStatus)
    {
      lockFile({
        variables: {
          input: {
            where: {
              id: clientFile.id,
            },
            data: {
              locked_at: null,
              locked_by: null,
            },
          },
        },
      });
    }
  }, [clientFile, viewStatus, lockFile]);

  const hideForm = (viewSummary || ["waitingSigning", "status", "cancelled"].includes(mode) || viewStatus);
  const noButtons = ["status", "waitingSigning", "cancelled"].includes(mode) || isAdmin(user);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const event = urlParams.get("event");
    setViewStatus(event === "signing_complete");
  }, []);

  return (
    <Layout>
      {fileDoesNotExist ? (
        <PageNotFound />
      ) : (
        <>
          <Helmet>
            <title>ADP Form</title>
          </Helmet>
          <div className={classNames(styles.transition)}>
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit, onError)}>
                <SaveHeader
                  formId={client_form.id}
                  fileStatus={status}
                  noButtons={noButtons}
                  hasSalesRep={salesRepAssigned}
                  summaryMode={viewSummary}
                  setViewStatus={setViewStatus}
                  formMode={mode}
                />
                <ApplicantHeader
                  name={name}
                  healthCard={healthCard}
                  clientFileId={clientFile.id}
                  noButtons={noButtons}
                />

                <div
                  className={classNames(styles.mainPage, {
                    [styles.noDisplay]: viewSummary || hideForm,
                  })}
                >
                  <div>
                    {!viewSummary && (
                      <FormNavigation
                        title="Application Sections"
                        sections={enabledSections}
                        selectedSection={selectedSection}
                        setSelectedSection={setSelectedSection}
                        waitingForInitialValidation={!initialized}
                      />
                    )}
                    {process.env.REACT_APP_DEBUG_TOOLS === "ENABLE" && (
                      <div style={{ position: "sticky", top: "750px" }}>
                        <br />
                        Debug Options{" "}
                        <button
                          type="button"
                          onClick={() => { setShowDebugInfo(!showDebugInfo); }}
                          style={{
                            border: "1px dashed red",
                            backgroundColor: "transparent",
                            fontSize: "14px",
                            cursor: "pointer",
                          }}
                        >
                          Toggle Debug Info
                        </button>
                        <br />
                        {showDebugInfo && (
                          <div>
                            <span> Form Status: {status}</span>
                            <br/>
                            <span> Form Mode: {mode}</span>
                            <pre style={{ fontSize: 10 }}>
                              {JSON.stringify(
                                methods.watch(
                                  selectedSection as ClientFormDotKeyType
                                ),
                                null,
                                1
                              )}
                            </pre>
                            <span style={{ fontSize: 10 }}>
                              ERRORS:
                              {inspect(methods.formState.errors)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <FormContent
                    title={sections[selectedSection].text}
                    readOnly={["view", "locked", "waitingSigning"].includes(mode)}
                  >
                    {Object.keys(sections).map((i) => (
                      <div
                        key={`section-${i}`}
                        className={classNames({
                          [styles.noDisplay]: i !== selectedSection,
                        })}
                      >
                        {initiateSigningMode && (
                          <NotificationBanner
                            warning
                            title="This form is waiting for signatures"
                            content="Form can not be submitted until all signatures are acquired. "
                            className={styles.bannerMargin}
                          />
                        )}
                        {sections[i].content}
                      </div>
                    ))}
                  </FormContent>
                </div>
                {viewSummary && !viewStatus && mode !== "status" && (
                  <div className={styles.summaryPage}>
                    <FormContent
                      title={summary.text}
                      readOnly={["view", "locked"].includes(mode)}
                    >
                      <div key="summary">
                        <Summary
                          setSelectedSection={(section) => {
                            setSelectedSection(section);
                            setViewSummary(false);
                          }}
                          salesRepStatus={salesRepAssigned}
                          readOnly={["view", "locked", "initiateSigning"].includes(mode)}
                        />
                      </div>
                    </FormContent>
                  </div>
                )}
                {mode === "status" && <SubmissionSummary />}
                {(viewStatus || ["waitingSigning", "cancelled"].includes(mode)) && <StatusScreen />}
              </form>
            </FormProvider>
          </div>
         </>
      )}
    </Layout>
  );
};

export default AdpForm;
